import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export const Testimonials = () => {
  const { t } = useLang();
  const items = t("testimonials.items") || [];

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start", slidesToScroll: 1 });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <section id="testimonials" className="relative py-24 lg:py-32 bg-[#F9F8F6]" data-testid="testimonials-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#8A5A44] mb-5">
              — {t("testimonials.eyebrow")}
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-3xl leading-[0.95]" data-testid="testimonials-title">
              {t("testimonials.title")}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={18} fill={i < 4 ? "#8A5A44" : "transparent"} stroke="#8A5A44" strokeWidth={1.4} />
              ))}
            </div>
            <span className="text-sm text-[#4A4A4A]">4,5 / 5 · Google</span>
          </div>
        </div>

        <div className="embla" ref={emblaRef}>
          <div className="embla__container">
            {items.map((it, i) => (
              <div
                key={i}
                className="embla__slide pl-0 lg:pr-6"
                style={{ flex: "0 0 90%", maxWidth: "90%" }}
                data-testid={`testimonial-${i}`}
              >
                <motion.article
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="h-full bg-white hairline p-8 lg:p-12 flex flex-col gap-6"
                >
                  <Quote size={36} strokeWidth={1.2} className="text-[#8A5A44]" />
                  <p className="font-display text-2xl lg:text-3xl font-light leading-snug tracking-tight text-[#1A1A1A]">
                    &ldquo;{it.text}&rdquo;
                  </p>
                  <div className="mt-auto flex items-center gap-3 pt-4 border-t border-[#DCD7D1]">
                    <div className="h-10 w-10 rounded-full bg-[#EFEBE6] grid place-items-center font-display text-[#8A5A44]">
                      {it.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium">{it.name}</div>
                      <div className="text-xs text-[#4A4A4A]">{it.role}</div>
                    </div>
                  </div>
                </motion.article>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center gap-4">
          <button
            onClick={() => emblaApi?.scrollPrev()}
            className="h-12 w-12 rounded-full border border-[#1A1A1A]/30 grid place-items-center hover:bg-[#1A1A1A] hover:text-[#F9F8F6] transition-colors"
            aria-label="Previous testimonial"
            data-testid="testimonials-prev-btn"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => emblaApi?.scrollNext()}
            className="h-12 w-12 rounded-full border border-[#1A1A1A]/30 grid place-items-center hover:bg-[#1A1A1A] hover:text-[#F9F8F6] transition-colors"
            aria-label="Next testimonial"
            data-testid="testimonials-next-btn"
          >
            <ChevronRight size={18} />
          </button>
          <div className="flex items-center gap-1.5 ml-2">
            {items.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === selectedIndex ? "w-8 bg-[#1A1A1A]" : "w-2 bg-[#1A1A1A]/25"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
